import React, { useCallback } from "react"
import { useFormContext, useController } from "react-hook-form"
import { ColorState, SwatchesPicker } from "react-color"
import { FormField } from "./types"
import { FormLabel, Tooltip } from "@mui/material"
import OutsideClickHandler from "react-outside-click-handler"
import styles from "./Forms.module.scss"
import { useBoolean } from "@web/hooks/useBoolean"
import { trackFormEvent } from "./utils"

export interface Props extends FormField {
  renderValue?: boolean

  onOpenChange?: (isOpen: boolean) => void
}

const ColorPicker = ({
  className,
  label,
  name,
  rules,
  renderValue = true,
  defaultValue,
  tracking,
  onOpenChange,
}: Props) => {
  const { control } = useFormContext()
  const controller = useController({ name, control, rules })
  const { state: isPickerOpen, setState: setIsPickerOpen } = useBoolean(false)

  const handleColorChange = useCallback(
    async ({ hex }: ColorState) => {
      controller.field.onChange(hex)

      if (tracking?.shouldTrackValueChange) {
        await trackFormEvent("Form Value Provided", tracking, name, label)
      }
    },
    [controller.field, label, name, tracking]
  )

  const handleClose = () => {
    setIsPickerOpen(false)
    onOpenChange?.(false)
  }

  const handleOpen = () => {
    setIsPickerOpen(true)
    onOpenChange?.(true)
  }

  const color = controller.field.value || defaultValue || "#000000"
  const isOpen = isPickerOpen

  return (
    <div className={className}>
      {label && <FormLabel>{label}</FormLabel>}

      <div className={styles.colorPreviewButtonWrapper}>
        <Tooltip
          title={
            <div>
              <OutsideClickHandler onOutsideClick={handleClose}>
                <div>
                  <SwatchesPicker color={color} onChange={handleColorChange} className={styles.colorPicker} />
                </div>
              </OutsideClickHandler>
            </div>
          }
          onClose={handleClose}
          open={isOpen}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          classes={{ tooltip: styles.colorPickerTooltip }}
        >
          <button
            style={{ backgroundColor: color }}
            className={styles.colorPreviewButton}
            type="button"
            onClick={handleOpen}
          />
        </Tooltip>

        {renderValue ? color : null}
      </div>
    </div>
  )
}

export default ColorPicker
